import { useState, useEffect, useMemo, useRef } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  getDirectory, getContratos, getContratoOpciones, crearContrato, editarContrato, borrarContrato,
  renovarContrato, anularRenovacion, getProjects,
  subirArchivosContrato, abrirArchivoContrato, borrarArchivoContrato,
} from '../../api/client'
import { useAuth } from '../../context/AuthContext'
import PageHeader, { Ico } from '../../components/PageHeader'
import { detalleError } from '../../components/admin/detalle'

// Administración > Contratos: the contracts of the people Buk doesn't
// manage (backend contratos.py) -- honorarios, plazo fijo. Admin and RRHH
// manage them, c_level reads them (for those two, Administración shows only
// this); nobody else reaches it. A person may hold several; a renewal
// extends the end date and stays as history. RRHH is told 30 and 15 days
// before one ends (the notification links here with ?contrato=<id>).
//
// Each contract opens in a tab of its own next to the list -- a new one, or
// one picked from it -- so what is being typed survives switching tabs; a
// tab closes when saved, or with its × (asking first if anything changed).

const VENCE_PRONTO = 30
const norm = (s) => (s || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase()
const clp = (n) => n == null ? '—' : `$${Number(n).toLocaleString('es-CL')}`
// "Contrato: Ana" -- the collaborator's first name, once one is picked.
const primerNombre = (nombre) => (nombre || '').trim().split(/\s+/)[0]
const fecha = (iso) => iso ? new Date(iso + 'T00:00:00').toLocaleDateString('es-CL') : '—'

function Vence({ t, c }) {
  if (c.vigencia !== 'activo' || c.dias_para_vencer == null) return null
  const d = c.dias_para_vencer
  if (d < 0) return <span className="ml-2 text-[11px] px-1.5 py-0.5 rounded bg-red-100 text-red-700">{t('contratos.vencido')}</span>
  if (d <= VENCE_PRONTO) return (
    <span className={`ml-2 text-[11px] px-1.5 py-0.5 rounded ${d <= 15 ? 'bg-amber-100 text-amber-800' : 'bg-yellow-50 text-yellow-800'}`}>
      {t('contratos.venceEn', { count: d })}
    </span>
  )
  return null
}

export default function AdminContratos() {
  const { t } = useTranslation(['personas', 'admin', 'common'])
  const { user } = useAuth()
  const roles = user?.roles || (user?.role ? [user.role] : [])
  const puedeEditar = roles.includes('admin') || roles.includes('rrhh')
  const [params, setParams] = useSearchParams()
  const abrirId = params.get('contrato')
  const onAbierto = () => setParams({}, { replace: true })
  const [personas, setPersonas] = useState([])
  const [lista, setLista] = useState(null)
  const [opciones, setOpciones] = useState(null)
  const [proyectos, setProyectos] = useState([])
  const [error, setError] = useState('')
  const [q, setQ] = useState('')
  const [vigencia, setVigencia] = useState('activo')
  const [tipo, setTipo] = useState('')
  const [tabs, setTabs] = useState([])               // [{ key, contrato, sucio }]; {} is a new one
  const [activa, setActiva] = useState('lista')
  const seq = useRef(0)

  const abrir = (c) => {
    const key = c.id ? `c-${c.id}` : `n-${++seq.current}`
    setTabs(ts => ts.some(x => x.key === key) ? ts : [...ts, { key, contrato: c, persona: c.persona, sucio: false }])
    setActiva(key)
  }
  const cerrar = (key, forzar = false) => {
    const tab = tabs.find(x => x.key === key)
    if (!forzar && tab?.sucio && !window.confirm(t('contratos.descartar'))) return
    setTabs(ts => ts.filter(x => x.key !== key))
    setActiva(a => a === key ? 'lista' : a)
  }
  const actualizar = (key, cambio) => setTabs(ts => ts.map(x => x.key === key ? { ...x, ...cambio } : x))

  // Leaving the page with something typed: the browser asks first.
  const haySucios = tabs.some(x => x.sucio)
  useEffect(() => {
    if (!haySucios) return
    const h = (e) => { e.preventDefault(); e.returnValue = '' }
    window.addEventListener('beforeunload', h)
    return () => window.removeEventListener('beforeunload', h)
  }, [haySucios])

  const cargar = () => getContratos().then(setLista).catch(e => { setLista([]); setError(detalleError(e, t('contratos.loadFailed'))) })
  useEffect(() => {
    cargar()
    getContratoOpciones().then(setOpciones).catch(() => {})
    getProjects().then(setProyectos).catch(() => {})
    getDirectory({ dias: 0 }).then(setPersonas).catch(() => {})
  }, [])   // eslint-disable-line react-hooks/exhaustive-deps

  // A notification's link: open that contract once the list is in.
  useEffect(() => {
    if (!abrirId || !lista) return
    const c = lista.find(x => String(x.id) === String(abrirId))
    if (c) abrir(c)
    onAbierto?.()
  }, [abrirId, lista])   // eslint-disable-line react-hooks/exhaustive-deps

  const visibles = useMemo(() => {
    const n = norm(q.trim())
    return (lista || []).filter(c =>
      (!vigencia || c.vigencia === vigencia) && (!tipo || c.tipo === tipo)
      && (!n || norm(c.persona).includes(n) || norm(c.proyecto).includes(n)))
  }, [lista, q, vigencia, tipo])

  const porVencer = (lista || []).filter(c => c.vigencia === 'activo' && c.dias_para_vencer != null
    && c.dias_para_vencer >= 0 && c.dias_para_vencer <= VENCE_PRONTO).length

  const selectCls = 'border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white'
  return (
    <div className="p-8">
      <div className="mb-6">
        <PageHeader title={t('contratos.tituloPagina')} subtitle={t('contratos.subtitulo')}
          actions={puedeEditar && (
            <button onClick={() => abrir({})}
              className="bg-gray-900 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-800 inline-flex items-center gap-1.5">
              <Ico.Plus /> {t('contratos.nuevo')}
            </button>
          )} />
      </div>
      <div className="flex gap-1 border-b border-gray-200 mb-6 overflow-x-auto">
        <button onClick={() => setActiva('lista')}
          className={`px-4 py-2 text-sm -mb-px border-b-2 whitespace-nowrap transition-colors ${
            activa === 'lista' ? 'border-gray-900 text-gray-900 font-medium' : 'border-transparent text-gray-500 hover:text-gray-800'}`}>
          {t('contratos.lista')}
        </button>
        {tabs.map(x => (
          <div key={x.key} className={`-mb-px border-b-2 inline-flex items-center whitespace-nowrap ${
            activa === x.key ? 'border-gray-900' : 'border-transparent'}`}>
            <button onClick={() => setActiva(x.key)}
              className={`pl-4 pr-1 py-2 text-sm inline-flex items-center gap-1.5 ${
                activa === x.key ? 'text-gray-900 font-medium' : 'text-gray-500 hover:text-gray-800'}`}>
              {x.persona ? t('contratos.tab', { nombre: primerNombre(x.persona) }) : t('contratos.nuevo')}
              {x.sucio && <span className="w-1.5 h-1.5 rounded-full bg-amber-500" title={t('contratos.sinGuardar')} />}
            </button>
            <button onClick={() => cerrar(x.key)} title={t('contratos.cerrar')}
              className="px-2 py-2 text-gray-400 hover:text-gray-800 text-sm leading-none">×</button>
          </div>
        ))}
      </div>

      <div className={activa === 'lista' ? '' : 'hidden'}>
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <input value={q} onChange={e => setQ(e.target.value)} placeholder={t('contratos.buscar')}
          className="border border-gray-200 rounded-lg px-3 py-2 text-sm w-72 max-w-full" />
        <select value={vigencia} onChange={e => setVigencia(e.target.value)} className={selectCls}>
          <option value="activo">{t('contratos.vigencia.activo')}</option>
          <option value="inactivo">{t('contratos.vigencia.inactivo')}</option>
          <option value="">{t('contratos.todos')}</option>
        </select>
        <select value={tipo} onChange={e => setTipo(e.target.value)} className={selectCls}>
          <option value="">{t('contratos.todosTipos')}</option>
          {Object.entries(opciones?.tipos || {}).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        {porVencer > 0 && <span className="text-xs text-amber-700">{t('contratos.porVencer', { count: porVencer })}</span>}
      </div>

      {error && <p className="mb-3 px-3 py-2 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600">{error}</p>}

      <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs text-gray-500 border-b border-gray-100">
              {['colaborador', 'tipo', 'proyecto', 'desde', 'hasta', 'valor', 'emisor', 'vigenciaCol'].map(h =>
                <th key={h} className="px-4 py-2.5 font-medium whitespace-nowrap">{t(`contratos.col.${h}`)}</th>)}
            </tr>
          </thead>
          <tbody>
            {lista == null && <tr><td colSpan={8} className="px-4 py-6 text-center text-gray-400">{t('common:loading', 'Cargando…')}</td></tr>}
            {lista != null && !visibles.length && <tr><td colSpan={8} className="px-4 py-6 text-center text-gray-400">{t('contratos.vacio')}</td></tr>}
            {visibles.map(c => (
              <tr key={c.id} onClick={() => abrir(c)} className="border-b border-gray-50 last:border-0 hover:bg-gray-50 cursor-pointer">
                <td className="px-4 py-2.5 font-medium text-gray-900 whitespace-nowrap">
                  {c.persona}{!c.persona_activa && <span className="ml-1 text-xs text-gray-400">({t('contratos.personaInactiva')})</span>}
                </td>
                <td className="px-4 py-2.5 whitespace-nowrap">{opciones?.tipos?.[c.tipo] || c.tipo}</td>
                <td className="px-4 py-2.5 text-gray-600">{c.proyecto || '—'}</td>
                <td className="px-4 py-2.5 whitespace-nowrap">{fecha(c.desde)}</td>
                <td className="px-4 py-2.5 whitespace-nowrap">
                  {fecha(c.hasta)}<Vence t={t} c={c} />
                  {c.renovaciones.length > 0 && <span className="ml-1 text-[11px] text-gray-400">↻{c.renovaciones.length}</span>}
                </td>
                <td className="px-4 py-2.5 whitespace-nowrap tabular-nums">{clp(c.valor_liquido)}</td>
                <td className="px-4 py-2.5">{opciones?.emisores?.[c.emisor] || '—'}</td>
                <td className="px-4 py-2.5">
                  <span className={`text-xs px-2 py-0.5 rounded-full ${c.vigencia === 'activo' ? 'bg-emerald-50 text-emerald-700' : 'bg-gray-100 text-gray-500'}`}>
                    {t(`contratos.vigencia.${c.vigencia}`)}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      </div>

      {/* Every open tab stays mounted, hidden when not shown: its form keeps what was typed. */}
      {tabs.map(x => (
        <div key={x.key} className={activa === x.key ? '' : 'hidden'}>
          <ContratoForm
            t={t} contrato={x.contrato} opciones={opciones} personas={personas || []} proyectos={proyectos}
            puedeEditar={puedeEditar}
            onSucio={(sucio) => actualizar(x.key, { sucio })}
            onPersona={(persona) => actualizar(x.key, { persona })}
            onCerrar={() => cerrar(x.key)}
            onListo={() => { cargar(); cerrar(x.key, true) }}
            onCambio={(c) => { cargar(); actualizar(x.key, { contrato: c }) }}
          />
        </div>
      ))}
    </div>
  )
}

const VACIO = {
  team_member_id: '', tipo: 'honorarios', desde: '', hasta: '', valor_liquido: '', emisor: '', estudios: '',
  direccion: '', banco_1: '', cuenta_1: '', tipo_cuenta_1: '', banco_2: '', cuenta_2: '', tipo_cuenta_2: '',
  vigencia: 'activo', project_id: '', notas: '',
}

// One contract's tab. onListo: saved or deleted, the tab closes. onCambio:
// the contract changed but the tab stays (an undone renewal, a removed file,
// a save that got halfway). onSucio: whether anything typed is unsaved.
//
// Renewal dates and PDFs typed in are kept here and go with "Guardar", for a
// new contract as for an existing one: the contract first, then each renewal
// (in end-date order), then the files.
function ContratoForm({ t, contrato, opciones, personas, proyectos, puedeEditar, onSucio, onPersona, onCerrar, onListo, onCambio }) {
  const nuevo = !contrato.id
  const desde = (c) => !c.id ? VACIO : Object.fromEntries(Object.keys(VACIO).map(k => [k, c[k] ?? '']))
  const [inicial, setInicial] = useState(() => JSON.stringify(desde(contrato)))
  const [form, setForm] = useState(() => desde(contrato))
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)
  const [renovs, setRenovs] = useState([])            // renewals typed in, not saved yet
  const [pdfs, setPdfs] = useState([])                // Files chosen, not uploaded yet
  const renovado = !nuevo && contrato.renovaciones.length > 0
  const soloVer = !puedeEditar

  const sucio = JSON.stringify(form) !== inicial || renovs.length > 0 || pdfs.length > 0
  useEffect(() => { onSucio(sucio) }, [sucio])   // eslint-disable-line react-hooks/exhaustive-deps

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }))

  // The person may have left (inactive): keep them in the list for this contract.
  const opcionesPersonas = useMemo(() => {
    const out = personas.map(p => ({ id: p.id, name: p.name }))
    if (!nuevo && !out.some(p => p.id === contrato.team_member_id))
      out.unshift({ id: contrato.team_member_id, name: contrato.persona })
    return out
  }, [personas, contrato, nuevo])

  // The tab is named after whoever is picked.
  useEffect(() => {
    onPersona(opcionesPersonas.find(p => String(p.id) === String(form.team_member_id))?.name || null)
  }, [form.team_member_id, opcionesPersonas])   // eslint-disable-line react-hooks/exhaustive-deps

  const guardar = async () => {
    if (renovs.some(r => !r.fecha || !r.hasta)) { setError(t('contratos.renovacionIncompleta')); return }
    setBusy(true); setError('')
    const body = Object.fromEntries(Object.entries(form).map(([k, v]) => [k, v === '' ? null : v]))
    for (const k of ['team_member_id', 'project_id', 'valor_liquido']) if (body[k] != null) body[k] = Number(body[k])
    let c = null
    try {
      c = nuevo ? await crearContrato(body) : await editarContrato(contrato.id, body)
      // Each renewal as it goes in leaves the pending list, so a retry after
      // a failure sends only what's left.
      for (const r of [...renovs].sort((a, b) => a.hasta.localeCompare(b.hasta))) {
        c = await renovarContrato(c.id, { fecha: r.fecha, hasta: r.hasta, notas: r.notas || null })
        setRenovs(rs => rs.filter(x => x !== r))
        setForm(f => ({ ...f, hasta: c.hasta }))
      }
      if (pdfs.length) {
        c = await subirArchivosContrato(c.id, pdfs)
        setPdfs([])
      }
      onListo()
      return
    } catch (e) {
      setError(detalleError(e, t('contratos.saveFailed')))
      if (c) {                                   // got halfway: the tab is that contract now
        setInicial(JSON.stringify(desde(c)))
        onCambio(c)
      }
    }
    setBusy(false)
  }

  const borrar = async () => {
    if (!window.confirm(t('contratos.confirmarBorrar', { persona: contrato.persona }))) return
    setBusy(true)
    try { await borrarContrato(contrato.id); onListo() } catch (e) { setError(detalleError(e, t('contratos.saveFailed'))); setBusy(false) }
  }

  const cambiarRenov = (i, k, v) => setRenovs(rs => rs.map((r, j) => j === i ? { ...r, [k]: v } : r))

  const elegirPdfs = (e) => {
    const nuevos = [...(e.target.files || [])]
    e.target.value = ''
    const malos = nuevos.filter(f => !/\.pdf$/i.test(f.name) && f.type !== 'application/pdf')
    if (malos.length) { setError(t('contratos.soloPdf', { nombres: malos.map(f => f.name).join(', ') })); return }
    setError('')
    setPdfs(ps => [...ps, ...nuevos])
  }

  const quitarArchivo = async (a) => {
    if (!window.confirm(t('contratos.confirmarQuitarArchivo', { nombre: a.filename }))) return
    setBusy(true); setError('')
    try { onCambio(await borrarArchivoContrato(a.id)) } catch (e) { setError(detalleError(e, t('contratos.saveFailed'))) }
    setBusy(false)
  }

  const abrirArchivo = async (a) => {
    try { await abrirArchivoContrato(a) } catch (e) { setError(detalleError(e, t('contratos.abrirFailed'))) }
  }

  const anular = async (r) => {
    if (!window.confirm(t('contratos.confirmarAnular'))) return
    setBusy(true); setError('')
    try {
      const c = await anularRenovacion(contrato.id, r.id)
      setForm(f => ({ ...f, hasta: c.hasta ?? '' }))
      setInicial(i => JSON.stringify({ ...JSON.parse(i), hasta: c.hasta ?? '' }))
      onCambio(c)
    } catch (e) { setError(detalleError(e, t('contratos.saveFailed'))) }
    setBusy(false)
  }

  const inp = 'w-full border border-gray-200 rounded-lg px-3 py-2 text-sm disabled:bg-gray-50 disabled:text-gray-700'
  const campo = (label, el, ancho = '') => (
    <label className={`block ${ancho}`}>
      <span className="block text-xs font-medium text-gray-600 mb-1">{label}</span>
      {el}
    </label>
  )
  const sel = (k, valores, { vacio = true, etiquetas } = {}) => (
    <select value={form[k]} onChange={set(k)} disabled={soloVer} className={inp}>
      {vacio && <option value="">—</option>}
      {valores.map(v => <option key={v} value={v}>{etiquetas ? etiquetas[v] : v}</option>)}
    </select>
  )
  const cuenta = (n) => (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
      {campo(t(`contratos.f.banco`, { n }), sel(`banco_${n}`, opciones?.bancos || []))}
      {campo(t(`contratos.f.cuenta`, { n }), <input value={form[`cuenta_${n}`]} onChange={set(`cuenta_${n}`)} disabled={soloVer} className={inp} />)}
      {campo(t(`contratos.f.tipoCuenta`, { n }), sel(`tipo_cuenta_${n}`, opciones?.tipos_cuenta || []))}
    </div>
  )

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6 max-w-4xl">
      <h2 className="text-base font-semibold text-gray-900 mb-4">
        {nuevo ? t('contratos.nuevo') : t('contratos.titulo', { persona: contrato.persona })}
      </h2>
      <div className="space-y-4">
        {error && <p className="px-3 py-2 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600">{error}</p>}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {campo(t('contratos.f.colaborador'), (
            <select value={form.team_member_id} onChange={set('team_member_id')} disabled={soloVer} className={inp}>
              <option value="">—</option>
              {opcionesPersonas.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          ))}
          {campo(t('contratos.f.tipo'), sel('tipo', Object.keys(opciones?.tipos || {}), { vacio: false, etiquetas: opciones?.tipos }))}
          {campo(t('contratos.f.desde'), <input type="date" value={form.desde} onChange={set('desde')} disabled={soloVer} className={inp} />)}
          {campo(renovado ? t('contratos.f.hastaRenovado') : renovs.length ? t('contratos.f.hastaOriginal') : t('contratos.f.hasta'),
            <input type="date" value={form.hasta} onChange={set('hasta')} disabled={soloVer || renovado} className={inp} />)}
          {campo(t('contratos.f.valor'), <input type="number" min="0" step="1" value={form.valor_liquido} onChange={set('valor_liquido')} disabled={soloVer} className={inp} />)}
          {campo(t('contratos.f.vigencia'), sel('vigencia', ['activo', 'inactivo'], { vacio: false,
            etiquetas: { activo: t('contratos.vigencia.activo'), inactivo: t('contratos.vigencia.inactivo') } }))}
          {campo(t('contratos.f.emisor'), sel('emisor', Object.keys(opciones?.emisores || {}), { etiquetas: opciones?.emisores }))}
          {campo(t('contratos.f.estudios'), sel('estudios', Object.keys(opciones?.estudios || {}), { etiquetas: opciones?.estudios }))}
          {campo(t('contratos.f.proyecto'), (
            <select value={form.project_id} onChange={set('project_id')} disabled={soloVer} className={inp}>
              <option value="">—</option>
              {proyectos.map(p => <option key={p.id} value={p.id}>{p.code ? `${p.code} · ${p.name}` : p.name}</option>)}
            </select>
          ), 'sm:col-span-2')}
          {campo(t('contratos.f.direccion'), <input value={form.direccion} onChange={set('direccion')} disabled={soloVer} className={inp} />, 'sm:col-span-2')}
        </div>

        <div className="space-y-3 pt-2 border-t border-gray-100">
          {cuenta(1)}
          {cuenta(2)}
        </div>

        {campo(t('contratos.f.notas'), <textarea rows={2} value={form.notas} onChange={set('notas')} disabled={soloVer} className={inp} />)}

        {form.tipo !== 'indefinido' && (
          <div className="pt-2 border-t border-gray-100">
            <h3 className="text-sm font-semibold text-gray-900 mb-1">{t('contratos.renovaciones')}</h3>
            <p className="text-xs text-gray-500 mb-2">{t('contratos.renovacionesAyuda')}</p>
            {!nuevo && !contrato.renovaciones.length && !renovs.length && (
              <p className="text-xs text-gray-400 mb-2">{t('contratos.sinRenovaciones')}</p>
            )}
            {!nuevo && contrato.renovaciones.map((r, i) => (
              <div key={r.id} className="flex items-center gap-3 text-sm py-1">
                <span className="text-gray-500 w-24">{fecha(r.fecha)}</span>
                <span>{fecha(r.hasta_anterior)} → <b>{fecha(r.hasta)}</b></span>
                {r.notas && <span className="text-xs text-gray-500 truncate">{r.notas}</span>}
                {puedeEditar && i === contrato.renovaciones.length - 1 && (
                  <button onClick={() => anular(r)} disabled={busy} className="ml-auto text-xs text-red-600 hover:underline">{t('contratos.anular')}</button>
                )}
              </div>
            ))}
            {renovs.map((r, i) => (
              <div key={i} className="flex flex-wrap items-end gap-2 mt-2 p-2 rounded-lg bg-amber-50/60 border border-amber-100">
                {campo(t('contratos.f.fechaRenovacion'), <input type="date" value={r.fecha} onChange={e => cambiarRenov(i, 'fecha', e.target.value)} className={inp} />)}
                {campo(t('contratos.f.nuevoHasta'), <input type="date" value={r.hasta} onChange={e => cambiarRenov(i, 'hasta', e.target.value)} className={inp} />)}
                {campo(t('contratos.f.notaRenovacion'), <input value={r.notas} onChange={e => cambiarRenov(i, 'notas', e.target.value)} className={inp} />, 'flex-1 min-w-[10rem]')}
                <button onClick={() => setRenovs(rs => rs.filter((_, j) => j !== i))} title={t('contratos.quitar')}
                  className="px-2 py-2 text-gray-400 hover:text-red-600 text-sm leading-none">×</button>
              </div>
            ))}
            {puedeEditar && (
              <button onClick={() => setRenovs(rs => [...rs, { fecha: '', hasta: '', notas: '' }])} disabled={busy}
                className="mt-2 text-sm text-gray-700 border border-dashed border-gray-300 rounded-lg px-3 py-1.5 hover:bg-gray-50">
                + {t('contratos.agregarRenovacion')}
              </button>
            )}
          </div>
        )}

        <div className="pt-2 border-t border-gray-100">
          <h3 className="text-sm font-semibold text-gray-900 mb-2">{t('contratos.documentos')}</h3>
          {!nuevo && !contrato.archivos.length && !pdfs.length && (
            <p className="text-xs text-gray-400 mb-2">{t('contratos.sinDocumentos')}</p>
          )}
          {!nuevo && contrato.archivos.map(a => (
            <div key={a.id} className="flex items-center gap-3 text-sm py-1">
              <button onClick={() => abrirArchivo(a)} className="text-blue-700 hover:underline truncate text-left">📄 {a.filename}</button>
              <span className="text-xs text-gray-400 shrink-0">{(a.size_bytes / 1024 / 1024).toFixed(1)} MB</span>
              {puedeEditar && (
                <button onClick={() => quitarArchivo(a)} disabled={busy} className="ml-auto text-xs text-red-600 hover:underline shrink-0">{t('contratos.quitar')}</button>
              )}
            </div>
          ))}
          {pdfs.map((f, i) => (
            <div key={i} className="flex items-center gap-3 text-sm py-1 text-gray-700">
              <span className="truncate">📄 {f.name}</span>
              <span className="text-xs text-amber-700 shrink-0">{t('contratos.porSubir')}</span>
              <button onClick={() => setPdfs(ps => ps.filter((_, j) => j !== i))} className="ml-auto text-xs text-gray-500 hover:text-red-600 shrink-0">{t('contratos.quitar')}</button>
            </div>
          ))}
          {puedeEditar && (
            <label className="mt-2 inline-block text-sm text-gray-700 border border-dashed border-gray-300 rounded-lg px-3 py-1.5 hover:bg-gray-50 cursor-pointer">
              + {t('contratos.adjuntarPdf')}
              <input type="file" accept="application/pdf,.pdf" multiple onChange={elegirPdfs} disabled={busy} className="hidden" />
            </label>
          )}
        </div>

        {puedeEditar && (
          <div className="flex items-center gap-2 pt-2 border-t border-gray-100">
            {!nuevo && <button onClick={borrar} disabled={busy} className="text-sm text-red-600 hover:underline">{t('contratos.borrar')}</button>}
            <button onClick={onCerrar} className="ml-auto px-4 py-2 text-sm text-gray-600 hover:text-gray-900">{t('common:cancel', 'Cancelar')}</button>
            <button onClick={guardar} disabled={busy || !form.team_member_id || !form.desde}
              className="bg-gray-900 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-800 disabled:opacity-50">
              {t('contratos.guardar')}
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
